import {
  Button,
  CircularProgress,
  Dialog,
  Stack,
  Typography,
} from '@mui/material';
import { format, isValid } from 'date-fns';
import * as Sentry from '@sentry/react';
import { useMemo, useState } from 'react';
import { useLastVisible } from '~/shared/hooks/useLastVisible';
import {
  defaultFeeSchedule,
  feeScheduleFromMeExpertRates,
  type FeeScheduleData,
} from '~/modules/connect/feeSchedule';
import { CENTS_PER_DOLLAR } from '~/shared/constants';
import { useToast } from '~/shared/contexts/ToastContext';
import { RetainerFeeType, type RetainerFieldsFragment } from '~/gql/graphql';
import { useRetainerMutations } from '~/modules/matters/useRetainerMutations';
import { UserRole } from '~/shared/types/connect';
import type { User } from '~/shared/types/connect';
import {
  getRateDisplayLabel,
  getRetainerHourlyRateDisplayContext,
  RetainerAgreementCheckbox,
  RetainerExpertCard,
  RetainerExpertCardAvatar,
  RetainerExpertCardContent,
  RetainerReceiptSummary,
  StyledDialogActions,
  StyledDialogContent,
  StyledDialogPaper,
} from '~/modules/matters/modals/shared';
import { ModalHeader } from '~/shared/components/ModalHeader';
import { styled } from '@mui/material/styles';
import { retainerCardDescription } from '~/modules/matters/detail/retainersTabUtils';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  matterId: string;
  retainer: RetainerFieldsFragment | null;
  getExpertById: (id: string) => User | undefined;
  viewOnly?: boolean;
  currentUserRole?: UserRole;
  isLoading?: boolean;
}

/**
 * The attorney confirms terms; `startRetainerFunding` then triggers a redirect to Stripe Checkout.
 * Final retainer approval (`APPROVED`) and matter–expert hire promotion run only after successful
 * payment, via backend webhooks—not in this client flow.
 */
const RetainerAgreementModal = ({
  isOpen,
  onClose,
  matterId,
  retainer: retainerProp,
  getExpertById,
  viewOnly,
  currentUserRole,
  isLoading = false,
}: Props) => {
  const { showToast } = useToast();
  const { startRetainerFunding, isStartingRetainerFunding } =
    useRetainerMutations();

  const [agreedToTerms, setAgreedToTerms] = useState(false);

  const retainer = useLastVisible(retainerProp, isOpen);

  const handleClose = () => {
    setAgreedToTerms(false);
    onClose();
  };

  const expertIdForFeeSchedule = retainer?.matterExpert.expertId;
  const feeSchedule: FeeScheduleData = useMemo(() => {
    const expertForSchedule = expertIdForFeeSchedule
      ? getExpertById(expertIdForFeeSchedule)
      : undefined;
    if (expertForSchedule?.role === UserRole.EXPERT) {
      return feeScheduleFromMeExpertRates({
        hourlyRate: expertForSchedule.hourlyRate,
        recordsReviewHourlyRate: expertForSchedule.recordsReviewHourlyRate,
        examinationHourlyRate: expertForSchedule.examinationHourlyRate,
        depositionHourlyRate: expertForSchedule.depositionHourlyRate,
        testimonyHourlyRate: expertForSchedule.testimonyHourlyRate,
      });
    }
    return defaultFeeSchedule(expertForSchedule?.hourlyRate ?? 0);
  }, [expertIdForFeeSchedule, getExpertById]);

  if (!retainer) {
    return (
      <Dialog
        open={isOpen}
        onClose={handleClose}
        PaperComponent={StyledDialogPaper}
        maxWidth="sm"
        fullWidth
      >
        <ModalHeader title="Retainer agreement" onClose={handleClose} />
        <StyledDialogContent>
          {isLoading ? (
            <RetainerLoadingContainer
              role="status"
              aria-live="polite"
              aria-label="Loading retainer"
            >
              <CircularProgress size={32} aria-hidden />
            </RetainerLoadingContainer>
          ) : (
            <Typography variant="body2" color="text.secondary">
              Request not found.
            </Typography>
          )}
        </StyledDialogContent>
        <StyledDialogActions>
          <Button onClick={handleClose}>Close</Button>
        </StyledDialogActions>
      </Dialog>
    );
  }

  const expertId = retainer.matterExpert.expertId;
  const expert = getExpertById(expertId);
  const isValidRole =
    currentUserRole === UserRole.ATTORNEY ||
    currentUserRole === UserRole.EXPERT;
  const isAttorney = isValidRole && currentUserRole === UserRole.ATTORNEY;
  const canSign =
    isValidRole && !viewOnly && isAttorney && !retainer.attorneyAgreedAt;

  const hoursParsed = (() => {
    const raw = retainer.hoursRequested;
    if (raw === null || raw === undefined || raw === '') {
      return 0;
    }
    const parsed = Number.parseFloat(raw);
    return Number.isFinite(parsed) ? parsed : 0;
  })();

  const isFlatRate =
    retainer.feeType === RetainerFeeType.FlatRate || hoursParsed === 0;
  const hourlyFeeDollars =
    retainer.hourlyFee !== null && retainer.hourlyFee !== undefined
      ? retainer.hourlyFee / CENTS_PER_DOLLAR
      : 0;
  const hourlyRateForLabel = isFlatRate ? 0 : hourlyFeeDollars;

  const hourlyRateDisplayContext = !isFlatRate
    ? getRetainerHourlyRateDisplayContext(retainer, feeSchedule)
    : null;
  const rateDisplayLabel =
    hourlyRateDisplayContext === null
      ? ''
      : getRateDisplayLabel(
          hourlyRateDisplayContext.rateKey,
          hourlyRateDisplayContext.customServiceLabel,
          hourlyRateForLabel,
        );
  const subtotalDollars = retainer.subtotal / CENTS_PER_DOLLAR;
  const platformFeeDollars = retainer.platformFee / CENTS_PER_DOLLAR;
  const totalDollars = retainer.total / CENTS_PER_DOLLAR;

  const handleSign = async () => {
    if (!canSign || isStartingRetainerFunding) {
      return;
    }
    try {
      const checkoutUrl = await startRetainerFunding(retainer.id);
      if (checkoutUrl) {
        window.location.assign(checkoutUrl);
        return;
      }
      showToast('Unable to start payment. Please try again.', 'error');
    } catch (error) {
      Sentry.captureException(error, {
        tags: { feature: 'start_retainer_funding' },
        extra: { matterId, retainerId: retainer.id },
      });
      const message =
        error instanceof Error
          ? error.message
          : 'Something went wrong. Please try again.';
      showToast(message, 'error');
    }
  };

  let modalTitle: string;
  if (viewOnly) {
    modalTitle = 'View agreement';
  } else if (isAttorney) {
    modalTitle = 'Approve & Fund';
  } else {
    modalTitle = 'Retainer agreement';
  }
  const subtitle =
    isAttorney && canSign
      ? 'Review the retainer details below. You must agree to the terms before approving and funding.'
      : undefined;

  const descriptionText = retainerCardDescription(retainer);

  return (
    <Dialog
      open={isOpen}
      onClose={handleClose}
      PaperComponent={StyledDialogPaper}
      maxWidth="sm"
      fullWidth
    >
      <ModalHeader
        title={modalTitle}
        subtitle={subtitle}
        onClose={handleClose}
      />
      <StyledDialogContent>
        <Stack spacing={2}>
          <RetainerExpertCard>
            <RetainerExpertCardAvatar
              avatarUrl={expert?.avatarUrl}
              initialsLabel={expert?.name ?? 'Expert'}
              alt=""
              aria-hidden
            />
            <RetainerExpertCardContent>
              {retainer.title && (
                <Typography variant="subtitle1" fontWeight={600}>
                  {retainer.title}
                </Typography>
              )}
              <Typography variant="body2" color="text.secondary">
                {expert?.name ?? 'Expert'}
                {isFlatRate ? ' · Flat fee' : ''}
              </Typography>
              {retainer.createdAt &&
                (() => {
                  const requestedDate = new Date(retainer.createdAt);
                  return isValid(requestedDate) ? (
                    <Typography variant="caption" color="text.secondary">
                      Requested {format(requestedDate, 'MMM d, yyyy')}
                    </Typography>
                  ) : null;
                })()}
            </RetainerExpertCardContent>
          </RetainerExpertCard>
          <Typography variant="body2">{descriptionText}</Typography>
          <RetainerReceiptSummary
            isFlatRate={isFlatRate}
            flatAmount={isFlatRate ? subtotalDollars : 0}
            rateDisplayLabel={rateDisplayLabel}
            hours={isFlatRate ? 0 : hoursParsed}
            subtotal={subtotalDollars}
            platformFee={isAttorney ? platformFeeDollars : undefined}
            total={isAttorney ? totalDollars : undefined}
          />
          {viewOnly && (
            <Typography variant="caption" color="text.secondary">
              Attorney {retainer.attorneyAgreedAt ? 'signed' : '—'} · Expert{' '}
              {retainer.expertAgreedAt ? 'signed' : '—'}
            </Typography>
          )}
          {!viewOnly && canSign && isAttorney && (
            <RetainerAgreementCheckbox
              agreedToTerms={agreedToTerms}
              onAgreedToTermsChange={setAgreedToTerms}
              includePlatformFees
            />
          )}
        </Stack>
      </StyledDialogContent>
      <StyledDialogActions>
        {viewOnly && <Button onClick={handleClose}>Close</Button>}
        {!viewOnly && canSign && (
          <Button
            variant="contained"
            onClick={handleSign}
            disabled={
              (isAttorney && !agreedToTerms) ||
              (isAttorney && isStartingRetainerFunding)
            }
            aria-busy={isStartingRetainerFunding}
            startIcon={
              isStartingRetainerFunding && (
                <CircularProgress size={16} color="inherit" aria-hidden />
              )
            }
          >
            {isStartingRetainerFunding ? 'Opening checkout…' : 'Fund Retainer'}
          </Button>
        )}
      </StyledDialogActions>
    </Dialog>
  );
};

const RetainerLoadingContainer = styled(Stack)(({ theme }) => ({
  alignItems: 'center',
  justifyContent: 'center',
  minHeight: 120,
  padding: theme.spacing(2),
}));

export default RetainerAgreementModal;
